import { ScorecardBody } from '@/components/ats/collect-score-card/ScorecardBody';
import { CenterSpinner } from '@/components/ats/loadders';
import { useShowToast, useQueryParams } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  useGetInterviewScorecardsQuery,
  useSubmitScoreCardMutation,
} from '@/store/query/ats-applications';
import {
  Button as ChakraButton,
  Center as ChakraCenter,
  Drawer as ChakraDrawer,
  DrawerBody as ChakraDrawerBody,
  DrawerContent as ChakraDrawerContent,
  DrawerFooter as ChakraDrawerFooter,
  DrawerHeader as ChakraDrawerHeader,
  DrawerOverlay,
  Flex as ChakraFlex,
  Icon,
} from '@chakra-ui/react';

// Cast complex polymorphic Chakra components to avoid TS2590
const Button = ChakraButton as any;
const Center = ChakraCenter as any;
const Drawer = ChakraDrawer as any;
const DrawerBody = ChakraDrawerBody as any;
const DrawerContent = ChakraDrawerContent as any;
const DrawerFooter = ChakraDrawerFooter as any;
const DrawerHeader = ChakraDrawerHeader as any;
const Flex = ChakraFlex as any;
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { AssessmentDetails } from './components';
import { useGetAssessmentQuery } from '@/store/query/assessment';
import { EmptyResults } from '@/components/ats/empty-results';
import { CsvFailed } from '@/assets/icons/csv-export';
import NoTask from '@/assets/icons/ats/NoTask';
import { KText } from '@/ui-library/components/text';
import { AutoSaveLoadingIndicator } from '@/components/ats/collect-score-card/AutoSaveLoadingIndicator';
import { useScorecardAutoSaveHelper } from '@/components/ats/collect-score-card/useScorecardAutoSaveHelper';
import { ViewScorecardNavButtons } from '@/components/ats/collect-score-card/ViewScorecardNavButtons';
import { X } from '@kula-ai/icons-core';

export const ReviewAssessment = ({
  isOpen = true,
  onClose = () => {},
  activity,
  application,
  showOnlyCurrUser = false,
  onSuccess = () => {},
  canFillForOthers = false,
  assessmentId = null,
  scorecardId = null,
  scAction = 'submit',
  isHackerrank: _isHackerrank = false,
  onNext = () => {},
  onPrev = () => {},
  showNavButtons = false,
  isFirstScorecard = false,
  isLastScorecard = false,
}: any) => {
  const formRef = useRef(null);
  const currUser = useSelector(selectCurrUser);
  const queryParams = useQueryParams();
  /** Tasks deep link query, e.g. `?ref=fill_assessment` */
  const refParam = queryParams.get('ref');

  const resolvedAssessmentId = activity?.data?.id || assessmentId;

  // Fetch assessment data first — its response may provide the applicationId
  const { data: assessmentData, isLoading: isAssessmentLoading } =
    useGetAssessmentQuery(
      { assessmentId: resolvedAssessmentId },
      { skip: !resolvedAssessmentId }
    );

  // Derive applicationId: prefer the prop, fall back to assessment response
  const applicationId = application?.id || assessmentData?.ats_application_id;

  const { ats_job } = application || {};
  const { job_status } = ats_job || {};

  const isJobUpdateAllowedStatus = !['closed', 'archived']?.includes(
    job_status
  );

  const { data, isLoading: isScLoading } = useGetInterviewScorecardsQuery(
    {
      applicationId,
      params: { ats_assessment_id: resolvedAssessmentId },
    },
    { skip: !resolvedAssessmentId || !applicationId }
  );
  const [submitScoreCard, { isLoading: isSubmitting }] =
    useSubmitScoreCardMutation();
  // Loading covers the full sequential flow:
  // 1. Assessment loading
  // 2. Waiting for applicationId to be derived from the assessment response
  // 3. Scorecards loading
  const isLoading =
    isAssessmentLoading ||
    isScLoading ||
    (!!resolvedAssessmentId && !applicationId);

  // When a specific scorecard is requested (view-individual mode), narrow the
  // list to that one entry so ScorecardBody doesn't render the full panel.
  const displayData =
    scorecardId && data
      ? data.filter((sc) => String(sc.id) === String(scorecardId))
      : data;

  const scoreCardReviewerIds = data?.map((sc) => sc?.reviewer?.id);
  const canSubmit =
    canFillForOthers || scoreCardReviewerIds?.includes(currUser?.id);
  const toast = useShowToast();
  const firstScorecard = (displayData ?? data)?.[0];
  const currUserScorecard = data?.find(
    (item) => item?.reviewer?.id === currUser?.id
  );
  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: applicationId,
    scorecardId: showOnlyCurrUser ? currUserScorecard?.id : data?.[0]?.id,
    questions: showOnlyCurrUser
      ? currUserScorecard?.questions
      : firstScorecard?.questions,
  });

  const closeDrawer = () => {
    // onClose from useScorecardTaskModal handles both disclosure + URL cleanup
    onClose();
  };

  const handleSubmit = async (values) => {
    const questions = data?.[0]?.questions;
    const currUserScorecard = data?.find(
      (item) => item?.reviewer?.id === currUser?.id
    );
    let scorecardId;
    if (showOnlyCurrUser) {
      scorecardId = currUserScorecard?.id ?? data?.[0]?.id;
    } else {
      scorecardId = data?.[0]?.id;
    }
    const payload = {
      rating: values?.rating,
      feedback: values?.feedback,
      questions: questions?.map((item, idx) => {
        const param: { question: any; answer?: any } = {
          question: item?.question,
        };
        if (values?.[idx + 1]) param.answer = values?.[idx + 1];
        return param;
      }),
      status: 'submitted',
    };
    const result = (await submitScoreCard({
      applicationId,
      scorecardId,
      payload,
    })) as any;
    if (result?.error?.length) {
      toast({ title: 'Scorecard submission failed', status: 'error' });
    } else {
      toast({ title: 'Scorecard submitted successfully' });
      onSuccess();
      closeDrawer();
    }
  };

  const isError = !assessmentData?.id || assessmentData?.status === 'cancelled';
  /** Tasks `?ref=fill_assessment`: valid assessment but no scorecard row for current user — keep assessment panel, scorecard column shows unavailable. */
  const isFillAssessmentMissingCurrUserScorecard =
    refParam === 'fill_assessment' && !isLoading && !currUserScorecard;
  const hideScorecardOnly =
    isFillAssessmentMissingCurrUserScorecard && !isError;
  /** Narrow drawer only for fully-unavailable assessment; split review stays wide. */
  const drawerWidth = !isLoading && isError ? '600px' : '90%';

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={closeDrawer}>
      <DrawerOverlay zIndex="1400" />
      <DrawerContent
        w={drawerWidth}
        maxW={drawerWidth}
        borderTopLeftRadius="xl"
      >
        {showNavButtons ? (
          <ViewScorecardNavButtons
            onClose={closeDrawer}
            onNext={onNext}
            onPrev={onPrev}
            isFirstScorecard={isFirstScorecard}
            isLastScorecard={isLastScorecard}
          />
        ) : (
          <Center
            w="48px"
            h="48px"
            left="-70px"
            top="32px"
            pos="absolute"
            bg="#fff"
            borderRadius="full"
            onClick={closeDrawer}
            cursor="pointer"
          >
            <Icon
              as={X}
              w="20px"
              h="20px"
              data-test-id="scorecard-drawer-close-icon"
            />
          </Center>
        )}
        <DrawerHeader
          p="22px 30px"
          borderBottom="1px solid"
          borderColor="border-color"
        >
          <KText size="md-medium" color="secondary-900">
            Review and {scAction} scorecard
          </KText>
        </DrawerHeader>
        <DrawerBody p="0" overflowY="hidden">
          <Flex h="100%">
            {isLoading ? (
              <CenterSpinner flex="1" />
            ) : isError ? (
              <EmptyResults
                icon={CsvFailed}
                wrapperProps={{ h: '100%', flex: 1 }}
                text={'This assessment is no longer available'}
                iconProps={{ h: '140px', w: '130px', mb: '0px' }}
                subText="NOT AVAILABLE"
              />
            ) : (
              <>
                <Flex
                  w="600px"
                  flexDir="column"
                  gap="12px"
                  p="12px"
                  borderRight="1px solid"
                  borderColor="border-color"
                  overflowY="auto"
                >
                  <AssessmentDetails
                    activity={activity}
                    application={application}
                    assessmentId={assessmentId}
                    candidate={firstScorecard?.candidate}
                    assessmentData={assessmentData}
                    platform={assessmentData?.platform}
                  />
                </Flex>
                <Flex flex="1" pt="" flexDir="column" overflowY="auto">
                  {hideScorecardOnly ? (
                    <EmptyResults
                      icon={NoTask}
                      wrapperProps={{ h: '100%', flex: 1 }}
                      subText="SCORECARD UNAVAILABLE"
                      subTextProps={{ color: 'secondary-600' }}
                      text="This scorecard is no longer accepting responses"
                      textProps={{ color: 'secondary-900' }}
                      iconProps={{ h: '140px', w: '130px', mb: '0px' }}
                    />
                  ) : firstScorecard?.id ? (
                    <ScorecardBody
                      scoreCardDetails={displayData ?? data}
                      formRef={formRef}
                      handleSubmit={handleSubmit as any}
                      onSuccess={onSuccess}
                      showOnlyCurrUser={showOnlyCurrUser}
                      canFillForOthers={canFillForOthers}
                      transcript={null}
                      isEdit={isJobUpdateAllowedStatus}
                      canSubmit={canSubmit}
                      skipHeader={true}
                      handleAutoSave={handleAutoSave}
                    />
                  ) : (
                    <EmptyResults
                      icon={NoTask}
                      wrapperProps={{ h: '100%' }}
                      subText="SCORECARD UNAVAILABLE"
                      subTextProps={{ color: 'secondary-600' }}
                      text="This scorecard is no longer accepting responses"
                      textProps={{ color: 'secondary-900' }}
                      iconProps={{ h: '140px', w: '130px', mb: '0px' }}
                    />
                  )}
                </Flex>
              </>
            )}
          </Flex>
        </DrawerBody>
        {firstScorecard &&
          !hideScorecardOnly &&
          !isError &&
          (data?.length === 1 || showOnlyCurrUser) &&
          isJobUpdateAllowedStatus &&
          (canFillForOthers || scoreCardReviewerIds?.includes(currUser?.id)) &&
          !isLoading && (
            <DrawerFooter borderTop="1px solid" borderColor="border-color">
              {isSaved && <AutoSaveLoadingIndicator isSaved={isSaved} />}
              <Button
                variant="secondary"
                mr={3}
                onClick={closeDrawer}
                data-test-id="secondary-cta-button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                onClick={() => {
                  if (formRef?.current) formRef?.current?.submitForm();
                }}
                data-test-id="primary-cta-button"
              >
                Submit scorecard
              </Button>
            </DrawerFooter>
          )}
      </DrawerContent>
    </Drawer>
  );
};
